import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Radio, ClipboardList, Truck, Settings, LogOut, Users, UserCog, AlertTriangle, BookOpen, LineChart,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LogoMark from "@/components/LogoMark";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "dispatcher"], end: true },
  { to: "/admin/dispatch", label: "Dispatch", icon: Radio, roles: ["admin", "dispatcher"] },
  { to: "/admin/bookings", label: "Bookings", icon: ClipboardList, roles: ["admin", "dispatcher"] },
  { to: "/admin/complaints", label: "Complaints", icon: AlertTriangle, roles: ["admin", "dispatcher"] },
  { to: "/admin/documentation", label: "Documentation", icon: BookOpen, roles: ["admin", "dispatcher"] },
  { to: "/admin/fleet", label: "Fleet", icon: Truck, roles: ["admin", "dispatcher"] },
  { to: "/admin/pricing", label: "Pricing", icon: Settings, roles: ["admin"] },
  { to: "/admin/pricing-reports", label: "Pricing Reports", icon: LineChart, roles: ["admin", "dispatcher"] },
  { to: "/admin/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/admin/settings", label: "Settings", icon: UserCog, roles: ["admin", "dispatcher"] },
];

export default function AdminLayout() {
  const { user, profile, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }
    if (!["admin", "dispatcher"].includes(user.role)) {
      navigate("/account", { replace: true });
    }
  }, [user, loading, navigate, location.pathname]);

  if (loading || !user || !["admin", "dispatcher"].includes(user.role)) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">
        Authorising…
      </div>
    );
  }

  const visibleNav = NAV.filter((n) => n.roles.includes(user.role));
  const onLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white flex">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="w-60 shrink-0 border-r border-white/10 bg-[#0B0B0C] flex flex-col">
        <Link to="/admin" className="px-5 py-5 border-b border-white/10 block">
          <LogoMark size="md" />
          <div className="text-[9px] uppercase tracking-[0.4em] text-[#8E9AA6] mt-2 text-center">Command Center</div>
        </Link>
        <nav className="flex-1 py-3">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors border-l-2 ${
                    isActive
                      ? "text-white border-white bg-white/[0.03]"
                      : "text-[#8E9AA6] border-transparent hover:text-white"
                  }`
                }
              >
                <Icon size={14} strokeWidth={1.5} /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-white/10 text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">
          © Phantasm
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col">
        <header className="sticky top-0 z-20 bg-[#0B0B0C]/95 backdrop-blur-xl border-b border-white/10 px-8 py-3.5 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8E9AA6]">Operations</div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm text-white font-light">
                {profile?.first_name} {profile?.last_name}
              </div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#8E9AA6]">{user.email}</div>
            </div>
            <span data-testid="admin-role-chip" className="text-[10px] uppercase tracking-[0.3em] text-[#0B0B0C] bg-[#E6E6E6] px-2.5 py-1">
              {user.role}
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="text-[#8E9AA6] hover:text-white transition-colors p-1"
              aria-label="Logout"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </header>
        <main className="flex-1 px-8 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
